/**
 * Hash a project's number into a hue, so the same project always draws the same cover —
 * no stored image, no network, no cost, and a project stays recognisable in the grid
 * from one visit to the next.
 *
 * FNV-1a: cheap, well spread over short strings like `2024-018`, and stable across
 * engines because `Math.imul` keeps the multiply in 32-bit.
 *
 * @param seed - Project number, or another stable identifier when it has none
 * @returns Hue in degrees, 0-359
 */
export function coverHue(seed: string): number {
  let hash = 2_166_136_261;

  // `for...of` walks code points, so `codePointAt` is the matching accessor — it also
  // keeps a surrogate pair as one value instead of hashing only its high half.
  for (const character of seed) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16_777_619);
  }

  return (hash >>> 0) % 360;
}

/**
 * Build the cover's background from a hue: three stops walking around the wheel, dark
 * enough that white text sits on any of them at well over 4.5:1.
 *
 * @param seed - Project number, or another stable identifier when it has none
 * @returns A CSS gradient
 */
export function getCoverGradient(seed: string): string {
  const hue = coverHue(seed);

  return [
    'linear-gradient(142deg,',
    `hsl(${hue} 55% 18%) 0%,`,
    `hsl(${(hue + 28) % 360} 45% 22%) 52%,`,
    `hsl(${(hue + 55) % 360} 40% 20%) 100%)`,
  ].join(' ');
}
